/**
 * Utility functions for file handling and Azure Storage compatibility
 */

/**
 * Sanitizes filename for Azure storage compatibility
 * Removes or replaces characters that are not allowed in Azure blob names
 */
export const sanitizeFilenameForAzure = (filename: string): string => {
  if (!filename) return '';
  
  // Get file extension
  const lastDotIndex = filename.lastIndexOf('.');
  const name = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
  
  // Replace problematic characters
  let sanitized = name
    // Remove or replace special characters that can cause issues
    .replace(/['"]/g, '') // Remove quotes
    .replace(/[#]/g, '-') // Replace hash with dash
    .replace(/[&]/g, 'and') // Replace ampersand with 'and'
    .replace(/[%]/g, 'percent') // Replace percent with 'percent'
    .replace(/[@]/g, 'at') // Replace @ with 'at'
    .replace(/[+]/g, 'plus') // Replace + with 'plus'
    .replace(/[=]/g, 'equals') // Replace = with 'equals'
    .replace(/[\s]+/g, '-') // Replace multiple spaces with single dash
    .replace(/[^\w\-_.]/g, '') // Remove any remaining special characters except word chars, dash, underscore, dot
    .replace(/-+/g, '-') // Replace multiple consecutive dashes with single dash
    .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  
  // Ensure name is not empty
  if (!sanitized) {
    sanitized = 'file';
  }
  
  // Combine name and extension
  return sanitized + extension;
};

/**
 * Sanitizes filename with additional safety measures
 */
export const sanitizeFilename = (filename: string): string => {
  const sanitized = sanitizeFilenameForAzure(filename);
  
  // Additional Azure-specific restrictions
  // Ensure filename doesn't start or end with dot
  let final = sanitized.replace(/^\.+|\.+$/g, '');
  
  // Ensure minimum length
  if (final.length < 1) {
    const extension = filename.includes('.') ? filename.substring(filename.lastIndexOf('.')) : '';
    final = 'document' + extension;
  }
  
  // Ensure maximum length (Azure blob names can be up to 1024 characters, but let's be conservative)
  if (final.length > 255) {
    const extension = final.includes('.') ? final.substring(final.lastIndexOf('.')) : '';
    const nameWithoutExt = final.substring(0, final.lastIndexOf('.') > 0 ? final.lastIndexOf('.') : final.length);
    final = nameWithoutExt.substring(0, 255 - extension.length) + extension;
  }
  
  return final;
};

/**
 * Sanitizes a file and returns a new file with a clean name compatible with Azure storage
 */
export const sanitizeFile = (file: File): File => {
  const sanitizedName = sanitizeFilename(file.name);
  
  // Create a new File object with the sanitized name
  const sanitizedFile = new File([file], sanitizedName, {
    type: file.type,
    lastModified: file.lastModified,
  });
  
  return sanitizedFile;
};

/**
 * Creates a new file with a sanitized name for Azure storage compatibility
 */
export const createFileWithSanitizedName = (file: File, sanitizedName?: string): File => {
  const finalName = sanitizedName || sanitizeFilenameForAzure(file.name);
  
  // Create a new File object with the sanitized name
  const sanitizedFile = new File([file], finalName, {
    type: file.type,
    lastModified: file.lastModified,
  });
  
  return sanitizedFile;
};

/**
 * Creates a unique filename by adding timestamp and random string
 * @param filename - The original filename
 * @returns A unique sanitized filename
 */
export const createUniqueFilename = (filename: string): string => {
  const sanitized = sanitizeFilenameForAzure(filename);
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  
  const lastDotIndex = sanitized.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${sanitized}_${timestamp}_${random}`;
  }
  
  const name = sanitized.substring(0, lastDotIndex);
  const extension = sanitized.substring(lastDotIndex);
  
  return `${name}_${timestamp}_${random}${extension}`;
};

/**
 * Validates if a filename is Azure storage compatible
 * @param filename - The filename to validate
 * @returns Object with isValid boolean and issues array
 */
export const validateAzureFilename = (filename: string): { isValid: boolean; issues: string[] } => {
  const issues: string[] = [];
  
  if (!filename) {
    issues.push('Filename cannot be empty');
    return { isValid: false, issues };
  }
  
  if (filename.length > 1024) {
    issues.push('Filename is too long (max 1024 characters)');
  }
  
  // Check for problematic characters
  if (/['"`]/.test(filename)) {
    issues.push('Filename contains quotes that may cause issues');
  }
  
  if (/[#%&+]/.test(filename)) {
    issues.push('Filename contains special characters (#, %, &, +) that may cause issues');
  }
  
  if (filename.trim() !== filename) {
    issues.push('Filename has leading or trailing spaces');
  }
  
  if (/[<>:"|?*]/.test(filename)) {
    issues.push('Filename contains invalid characters (< > : " | ? *)');
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Format file size to human readable format
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if file type is an image
 * @param fileType - MIME type of the file
 * @returns Boolean indicating if it's an image
 */
export const isImageFile = (fileType: string): boolean => {
  return fileType.startsWith('image/');
};

/**
 * Get appropriate icon name for file type
 * @param fileType - MIME type of the file
 * @returns Icon identifier string
 */
export const getFileTypeIcon = (fileType: string): string => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType.includes('word')) return 'document';
  if (fileType.includes('excel') || fileType.includes('sheet')) return 'spreadsheet';
  if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('archive')) return 'archive';
  return 'file';
};
